import React, { useState, useContext, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import {
  Edit as EditIcon,
  Print as PrintIcon,
  FileCopyRounded as FileCopyRoundedIcon,
  Lock as LockIcon,
} from '@mui/icons-material'

import PrintDialog from 'components/generic/printable/PrintDialog'
import PrintablePurchaseOrder from 'components/generic/printable/PrintablePurchaseOrder'
import PrintableSalesOrder from 'components/generic/printable/PrintableSalesOrder'
import { ACTION_TYPE } from 'components/generic/buttons/ButtonList'
import { trackEvent } from 'utils/tracking'

import { Typography, Stack, Tooltip } from '@mui/material'
import { Chip } from 'components/generic/chip'
import ContentLayoutContext from 'components/layouts/ContentLayoutContextProvider'
import TabsLayoutContext from 'components/layouts/TabsLayoutContextProvider'
import { CHURN_ZERO_EVENTS as cz } from 'utils/constants'
import { setDuplicatePurchaseOrderId } from 'state/purchaseOrder'
import { setDuplicateSalesOrderId } from 'state/salesOrder'
import { generateLockTooltip } from 'utils/helpers'

export const useContractHeader = ({ isSales, contractData, isInLockedAccountingPeriodV2 }) => {
  const contractType = isSales ? 'Sales' : 'Purchase'
  const isArchived = !!contractData?.archivedAt
  const [activeDialog, setActiveDialog] = useState(null)
  const contentContext = useContext(ContentLayoutContext)
  const tabsContext = useContext(TabsLayoutContext)
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const handleClosePrintDialog = () => setActiveDialog(null)
  const handleAfterPrint = () => trackEvent(isSales ? cz.PRINT_SALES_ORDER : cz.PRINT_PURCHASE_ORDER)
  const handleOpenPrintDialog = () =>
    setActiveDialog({
      id: 'scale-full',
      title: `${contractType} Order #${contractData[`${contractType.toLowerCase()}OrderTicketId`]}`,
    })

  const handleDuplicate = async () => {
    const setDuplicateContractTypeOrderId = isSales ? setDuplicateSalesOrderId : setDuplicatePurchaseOrderId
    dispatch(setDuplicateContractTypeOrderId(contractData?.id))
    trackEvent(cz[`DUPLICATE_${contractType.toUpperCase()}_ORDER`])
    navigate(`/app/contracts/${contractType.toLowerCase()}-orders/new`)
  }

  const pageActions = [
    {
      text: 'Edit',
      icon: <EditIcon />,
      id: `edit-${contractType.toLowerCase()}-order-button`,
      onClick: () => navigate(`/app/contracts/${contractType.toLowerCase()}-orders/${contractData?.id}/edit`),
      disabled: isArchived,
    },
    {
      text: 'Print',
      icon: <PrintIcon />,
      variant: 'text',
      id: 'print-button',
      type: ACTION_TYPE.ActionButton,
      onClick: handleOpenPrintDialog,
    },
    {
      text: 'Duplicate',
      icon: <FileCopyRoundedIcon />,
      variant: 'text',
      id: 'duplicate-button',
      type: ACTION_TYPE.ActionButton,
      onClick: handleDuplicate,
    },
  ]

  useEffect(() => {
    tabsContext.setContainerTabsVisibility(false)
    contentContext.setContainerActions(pageActions)

    if (contractData) {
      contentContext.setContainerTitle(
        <Stack direction="row" alignItems="center" spacing={1}>
          {isInLockedAccountingPeriodV2 && (
            <Tooltip title={generateLockTooltip(`${contractType.toLowerCase()} order`, false)} placement="top">
              <LockIcon color="info" />
            </Tooltip>
          )}
          <Typography variant="h5">
            {contractType} Order #{contractData[`${contractType.toLowerCase()}OrderTicketId`]}
          </Typography>
          <Chip label={isArchived ? 'Archived' : contractData?.status || ''} />
        </Stack>,
      )
    }
  }, [contractData])

  const COPrintDialog = props => {
    if (!activeDialog) return null
    return (
      <PrintDialog
        title={activeDialog.title}
        open={!!activeDialog}
        onClose={handleClosePrintDialog}
        showLogoAddress
        onAfterPrint={handleAfterPrint}
      >
        {isSales ? <PrintableSalesOrder {...props} /> : <PrintablePurchaseOrder {...props} />}
      </PrintDialog>
    )
  }
  COPrintDialog.propTypes = {
    orderData: PropTypes.object,
    materials: PropTypes.array,
  }

  COPrintDialog.defaultProps = {
    orderData: {},
    materials: [],
  }

  return {
    COPrintDialog,
  }
}
